import clsx from 'clsx'
import React from 'react'

import {css} from '../theme/css'
import {makeStyles} from '../theme/UNSTABLE_makeStyles'
import {SvgIcon, SvgIconProps} from './SvgIcon'

export const createLargeSvgIcon = (children: React.ReactNode, displayName: string): typeof SvgIcon => {
  const useClasses = makeStyles(
    {
      root: css`
        font-size: 40px;
      `,
    },
    {name: displayName},
  )
  const Component = (props: SvgIconProps, ref: React.Ref<SVGSVGElement>) => (
    <SvgIcon ref={ref} classes={useClasses()} viewBox="0 0 40 40" {...props}>
      {children}
    </SvgIcon>
  )

  if (process.env.NODE_ENV !== 'production') {
    // Need to set `displayName` on the inner component for React.memo.
    // React prior to 16.14 ignores `displayName` on the wrapper.
    Component.displayName = `${displayName}Logo`
  }

  return React.memo(React.forwardRef(Component))
}

const classes = {
  rtlTransform: css`
    [dir='rtl'] & {
      transform: rotate(180deg);
    }
  `,
}

type CreateSvgIconOptions = {
  viewBox?: string
  /**
   * Флаг для отзеркаливания иконки в rtl версии
   */
  rtlTransform?: boolean
}

export const createSvgIcon = (
  path: React.ReactElement,
  displayName: string,
  options?: CreateSvgIconOptions,
): typeof SvgIcon => {
  const Component = (props: SvgIconProps, ref: React.Ref<SVGSVGElement>) => {
    const svgProps = {
      ref,
      ...props,
      className: clsx(options?.rtlTransform && classes.rtlTransform, props.className),
      ...(options?.viewBox && {
        viewBox: options.viewBox,
      }),
    }

    return React.createElement(SvgIcon, svgProps, path)
  }

  if (process.env.NODE_ENV !== 'production') {
    // Need to set `displayName` on the inner component for React.memo.
    // React prior to 16.14 ignores `displayName` on the wrapper.
    Component.displayName = `${displayName}Icon`
  }

  return React.memo(React.forwardRef(Component))
}

export type IconType = ReturnType<typeof createSvgIcon>
